import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, share } from 'rxjs/operators';

// https://medium.com/@vikeshm/data-caching-angular-http-interceptor-2d87f95e2340
class CachingInterceptor {
    static { this.CACHE_HEADER = 'AngCache'; }
    static { this.CACHE_HEADER_RESET = 'AngCacheReset'; }
    // je staticka protoze pri pouziti v modulech obcas byva jina instance interceptoru
    // timto se vyresi aby i pri vice instancich bylo jedno cache uloziste
    static { this.cachedData = new Map(); }
    static { this.config = {
        debug: false,
        // cacheSize - maximalni pocet zaznamu v cache
        cacheSize: 1000,
        // garbageCollectorTarget - jaky ma byt cilovy stav zaplnenosti cache po procisteni
        garbageCollectorTarget: 0.7,
    }; }
    static garbageCollector() {
        if (CachingInterceptor.cachedData.size > CachingInterceptor.config.cacheSize) {
            const cacheTargetSize = Math.round(CachingInterceptor.config.cacheSize *
                CachingInterceptor.config.garbageCollectorTarget);
            CachingInterceptor.log('cache garbageCollector target: ', cacheTargetSize);
            // seradime dle posledniho pouziti
            const sorted = new Map([...CachingInterceptor.cachedData.entries()].sort((a, b) => a[1].lastHit - b[1].lastHit));
            // a mazeme dokud nemame cilovou velikost
            sorted.forEach((c, key) => {
                if (CachingInterceptor.cachedData.size > cacheTargetSize) {
                    CachingInterceptor.cachedData.delete(key);
                }
            });
            // CachingInterceptor.log("cache after garbageCollector: ", CachingInterceptor.cachedData);
        }
    }
    static cloneRequestWithoutCacheHeader(req) {
        const newHeaders = req.headers.delete(CachingInterceptor.CACHE_HEADER);
        return req.clone({ headers: newHeaders });
    }
    static getValidAfter(httpRequest) {
        const cacheHeader = httpRequest.headers.get(CachingInterceptor.CACHE_HEADER);
        if (cacheHeader == null || cacheHeader == 'undefined') {
            return null;
        }
        const cacheTimeout = parseInt(cacheHeader);
        return Date.now() - cacheTimeout * 1000;
    }
    static saveRequest(stateEvent, uniqueUrl) {
        if (stateEvent instanceof HttpResponse) {
            CachingInterceptor.cachedData.delete(uniqueUrl);
            if (stateEvent.status == 200) {
                CachingInterceptor.log('cache cached', uniqueUrl);
                CachingInterceptor.cachedData.set(uniqueUrl, {
                    response: stateEvent.clone(),
                    timestamp: Date.now(),
                    lastHit: Date.now(),
                });
            }
            else {
                CachingInterceptor.log('cache cached skipped (bad status code)', uniqueUrl);
            }
        }
    }
    static getStingFromAny(x) {
        if (x !== null) {
            if (typeof x === 'string') {
                return x;
            }
            else {
                return JSON.stringify(x);
            }
        }
        else {
            return '';
        }
    }
    static hash(x) {
        // https://stackoverflow.com/a/7616484
        let hash = 0, i, chr;
        if (x.length === 0)
            return hash + '';
        for (i = 0; i < x.length; i++) {
            chr = x.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash + '';
    }
    static log(...message) {
        if (CachingInterceptor.config.debug) {
            console.log(...message);
        }
    }
    intercept(httpRequest, handler) {
        const validAfter = CachingInterceptor.getValidAfter(httpRequest);
        const httpRequestWithoutCache = CachingInterceptor.cloneRequestWithoutCacheHeader(httpRequest);
        // create unique index
        let uniqueUrl = httpRequestWithoutCache.urlWithParams;
        const body = CachingInterceptor.getStingFromAny(httpRequestWithoutCache.body);
        CachingInterceptor.log('cache body', body);
        const bodyHash = CachingInterceptor.hash(body);
        CachingInterceptor.log('cache body hash', bodyHash);
        uniqueUrl += bodyHash;
        CachingInterceptor.log('cache index', uniqueUrl);
        CachingInterceptor.garbageCollector();
        if (!validAfter ||
            httpRequest.headers.get(CachingInterceptor.CACHE_HEADER_RESET)) {
            CachingInterceptor.cachedData.delete(uniqueUrl);
            return handler
                .handle(httpRequestWithoutCache)
                .pipe(tap((x) => CachingInterceptor.saveRequest(x, uniqueUrl)));
        }
        const lastResponse = CachingInterceptor.cachedData.get(uniqueUrl);
        if (lastResponse) {
            if (lastResponse.timestamp < validAfter) {
                CachingInterceptor.log('cache autodelete', uniqueUrl);
                CachingInterceptor.cachedData.delete(uniqueUrl);
            }
            else {
                // In case of parallel requests to same URI,
                // return the request already in progress
                // otherwise return the last cached data
                lastResponse.lastHit = Date.now();
                return lastResponse.response instanceof Observable
                    ? lastResponse.response
                    : of(lastResponse.response.clone());
            }
        }
        // If the request of going through for first time
        // then let the request proceed and cache the response
        const requestHandle = handler.handle(httpRequestWithoutCache).pipe(tap((x) => CachingInterceptor.saveRequest(x, uniqueUrl)), share());
        // Meanwhile cache the request Observable to handle parallel request
        CachingInterceptor.cachedData.set(uniqueUrl, {
            response: requestHandle,
            timestamp: Date.now(),
            lastHit: Date.now(),
        });
        return requestHandle;
    }
    clear(match = null) {
        if (match == null) {
            CachingInterceptor.cachedData.clear();
        }
        else {
            CachingInterceptor.cachedData.forEach((value, key) => {
                if (key.includes(match)) {
                    CachingInterceptor.cachedData.delete(key);
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CachingInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CachingInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CachingInterceptor, decorators: [{
            type: Injectable
        }] });

class CacheModule {
    static forRoot() {
        return {
            ngModule: CacheModule,
            providers: [CachingInterceptor],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CacheModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.7", ngImport: i0, type: CacheModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CacheModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: CacheModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CacheModule, CachingInterceptor };
